// 全局快捷键：g + 字母 跳转主路由。
// 仅注册一次（在 __root.tsx）。当焦点在 input/textarea/contenteditable 时不响应，避免吃字符。
//
// 当前绑定：
//   g d → /            （dashboard）
//   g m → /machines
//   g a → /accounts
//   g i → /instances
//   g g → /games
//
// 默认用 window.location.assign 做全页跳转，保证快捷键在任意页面状态下都能回到
// 对应 SPA 路由；测试可通过 options.navigate 注入替代实现。

import { useEffect } from "react";

type ShortcutMap = Record<string, string>;

const DEFAULT_MAP: ShortcutMap = {
  d: "/",
  m: "/machines",
  a: "/accounts",
  i: "/instances",
  g: "/games",
};

const PREFIX_RESET_MS = 1500;

function isEditable(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (target.isContentEditable) return true;
  return false;
}

interface UseGlobalShortcutsOptions {
  navigate?: (to: string) => void; // 测试用：覆盖默认 location.assign
  shortcuts?: ShortcutMap;
}

export function useGlobalShortcuts(options: UseGlobalShortcutsOptions = {}): void {
  useEffect(() => {
    const navigate = options.navigate ?? ((to: string) => window.location.assign(to));
    const shortcuts = options.shortcuts ?? DEFAULT_MAP;

    let primed = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    function resetPrime() {
      primed = false;
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    }

    function onKeyDown(e: KeyboardEvent) {
      if (e.altKey || e.ctrlKey || e.metaKey) return;
      if (isEditable(e.target)) return;

      if (!primed) {
        if (e.key === "g") {
          primed = true;
          timer = setTimeout(resetPrime, PREFIX_RESET_MS);
        }
        return;
      }

      // primed 状态：匹配第二键
      const target = shortcuts[e.key];
      resetPrime();
      if (target) {
        e.preventDefault();
        navigate(target);
      }
    }

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      if (timer) clearTimeout(timer);
    };
  }, [options.navigate, options.shortcuts]);
}

export const SHORTCUTS = DEFAULT_MAP;
